import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/shared/Sidebar';
import DataGrid, { GridColumn } from '../components/data/DataGrid';
import FinancialRecordFilterPanel from '../components/data/FinancialRecordFilterPanel';
import ValidationRuleTable from '../components/validationRule/ValidationRuleTable';
import financialRecordKPIService from '../services/financialRecordKPIService';
import { FinancialRecord, FinancialRecordFilter } from '../types/data/financialRecord';
import { ValidationRule } from '../types/ValidationRule';
import ValidationRuleService from '../services/validationRuleService';
import { generateValidationRules, ValidationRuleGenerationRequest } from '../services/bedrockApi';
import useAuthStore from '../store/authStore';
import { User } from '../types/user';
import { UserService } from '../services/userService';
import './GenerateAIValidationsPage.css';

const GenerateAIValidationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  
  // State management for data table
  const [records, setRecords] = useState<FinancialRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FinancialRecordFilter>({});
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string | number>>(new Set());
  const [allRecordsForFilters, setAllRecordsForFilters] = useState<FinancialRecord[]>([]);
  const [userMap, setUserMap] = useState<Map<string, User>>(new Map());
  const [filterUserIds, setFilterUserIds] = useState<string[]>([]);
  
  // State management for input form
  const [failureRate, setFailureRate] = useState<string>('');
  const [numberOfRules, setNumberOfRules] = useState<string>('20');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedRules, setGeneratedRules] = useState<ValidationRule[]>([]);
  const [generationError, setGenerationError] = useState<string | null>(null);

  // Define columns for the data grid
  const columns: GridColumn[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'brand', headerName: 'Brand', width: 120 },
    { field: 'dealer', headerName: 'Dealer', width: 200 },
    { field: 'groups', headerName: 'Groups', width: 150, 
      valueFormatter: (value) => Array.isArray(value) ? value.join(', ') : value || '' },
    { field: 'year', headerName: 'Year', width: 100, type: 'number' },
    { field: 'month', headerName: 'Month', width: 100, type: 'number' },
    { field: 'kpiCount', headerName: 'KPI Count', width: 120, type: 'number' },
    { field: 'ownerName', headerName: 'Owner', width: 150 },
    { 
      field: 'updatedAt', 
      headerName: 'Last Updated', 
      width: 160, 
      type: 'date',
      valueFormatter: (value) => {
        if (!value) return '';
        return new Date(value).toLocaleString();
      }
    }
  ];

  // Transform records for the data grid
  const transformedRecords = records.map(record => {
    // Get owner name from userMap if available
    let ownerName = '';
    if (record.userId) {
      // Ensure consistent userId format for lookup
      const userId = String(parseInt(String(record.userId)));
      const user = userMap.get(userId);
      ownerName = user?.name || (record as any).ownerName || userId;
    }

    return {
      ...record,
      kpiCount: record.kpis ? record.kpis.length : 0,
      groups: record.groups || [],
      ownerName // Add owner name to transformed record
    };
  });

  // Fetch records function
  const fetchRecords = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Build the filter
      const apiFilter = {
        ...filter,
        page: currentPage,
        pageSize: pageSize,
        owners: filter.owners // Make sure owners filter is included
      };

      const response = await financialRecordKPIService.getFinancialRecords(apiFilter);
      
      setRecords(response.records || []);
      setTotalCount(response.totalCount || 0);
      
    } catch (err: any) {
      setError(err.message || 'Failed to fetch records');
      setRecords([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle row expansion
  const handleRowExpand = (rowId: string | number) => {
    const newExpandedRowIds = new Set(expandedRowIds);
    newExpandedRowIds.add(rowId);
    setExpandedRowIds(newExpandedRowIds);
  };

  // Handle row collapse
  const handleRowCollapse = (rowId: string | number) => {
    const newExpandedRowIds = new Set(expandedRowIds);
    newExpandedRowIds.delete(rowId);
    setExpandedRowIds(newExpandedRowIds);
  };

  // Render expanded row content (KPI details)
  const renderExpandedRow = (row: any) => {
    const record = records.find(r => r.id === row.id);
    if (!record || !record.kpis || record.kpis.length === 0) {
      return (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
          No KPI data available for this record.
        </div>
      );
    }
    
    return (
      <div style={{ padding: '20px', background: '#f8f9fa' }}>
        <h4 style={{ marginBottom: '15px', color: '#333' }}>📊 KPI Details</h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '15px' }}>
          {record.kpis.map((kpi, index) => (
            <div key={index} style={{ 
              background: 'white', 
              padding: '15px', 
              borderRadius: '8px', 
              border: '1px solid #e9ecef' 
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '8px' 
              }}>
                <span style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  background: '#f8f9fa', 
                  padding: '2px 6px', 
                  borderRadius: '4px' 
                }}>
                  {kpi.code}
                </span>
                {kpi.category && (
                  <span style={{ 
                    fontSize: '12px', 
                    color: '#007bff', 
                    background: 'rgba(0, 123, 255, 0.1)', 
                    padding: '2px 6px', 
                    borderRadius: '4px' 
                  }}>
                    {kpi.category}
                  </span>
                )}
              </div>
              
              <div style={{ 
                fontWeight: 'bold', 
                color: '#333', 
                marginBottom: '8px', 
                fontSize: '16px' 
              }}>
                {kpi.name || 'Unnamed KPI'}
              </div>
              
              <div style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                color: '#007bff' 
              }}>
                {typeof kpi.value === 'number' 
                  ? kpi.value.toLocaleString() 
                  : kpi.value || 'N/A'}
                {kpi.unit && <span style={{ fontSize: '14px', color: '#666', marginLeft: '4px' }}>{kpi.unit}</span>}
              </div>
              
              {kpi.description && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#666', 
                  marginTop: '8px' 
                }}>
                  {kpi.description}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Handle filter application
  const handleApplyFilter = useCallback((newFilter: FinancialRecordFilter) => {
    setFilter(newFilter);
    setCurrentPage(1); // Reset to first page when filtering
    // Clear expanded rows when filter changes
    setExpandedRowIds(new Set());
  }, []);

  // Handle filter clearing
  const handleClearFilter = useCallback(() => {
    setFilter({});
    setCurrentPage(1); // Reset to first page when clearing filter
    // Clear expanded rows when filter is cleared
    setExpandedRowIds(new Set());
  }, []);

  // Handle page changes
  const handlePageChange = useCallback((page: number) => {
    if (page >= 1 && page <= Math.ceil(totalCount / pageSize)) {
      setCurrentPage(page);
      // Clear expanded rows when page changes
      setExpandedRowIds(new Set());
    }
  }, [totalCount, pageSize]);

  // Calculate total pages
  const totalPages = Math.ceil(totalCount / pageSize);

  // Update filter options from records
  const updateFilterOptionsFromRecords = useCallback(async () => {
    try {
      // Fetch ALL records with current filter to get available filter options
      const allRecordsFilter: any = { page: 1, pageSize: 10000 };
      
      // Apply current filters but remove pagination limits
      if (filter.brands && filter.brands.length > 0) allRecordsFilter.brands = filter.brands;
      if (filter.dealers && filter.dealers.length > 0) allRecordsFilter.dealers = filter.dealers;
      if (filter.years && filter.years.length > 0) allRecordsFilter.years = filter.years;
      if (filter.months && filter.months.length > 0) allRecordsFilter.months = filter.months;
      if (filter.kpiCodes && filter.kpiCodes.length > 0) allRecordsFilter.kpiCodes = filter.kpiCodes;
      if (filter.kpiCategories && filter.kpiCategories.length > 0) allRecordsFilter.kpiCategories = filter.kpiCategories;
      if (filter.owners && filter.owners.length > 0) allRecordsFilter.owners = filter.owners;
      
      const response = await financialRecordKPIService.getFinancialRecords(allRecordsFilter);
      setAllRecordsForFilters(response.records || []);
      
      // Fetch users for owner names (only for admins and group admins)
      if (user?.role === 'admin' || user?.role === 'group_admin') {
        const users = await UserService.getAllUsers();
        const userMap = new Map<string, User>();
        const userIds: string[] = [];
        
        users.forEach((fetchedUser: User) => {
          const userId = String(parseInt(String(fetchedUser.id)));
          userMap.set(userId, fetchedUser);
          userIds.push(userId);
        });
        
        setUserMap(userMap);
        setFilterUserIds(userIds);
      } else {
        setFilterUserIds([]);
        setUserMap(new Map());
      }
    } catch (error) {
      console.error('Error fetching records for filter options:', error);
    }
  }, [user?.role, filter]);

  // Fetch records when filter, page changes (this handles initial load too)
  useEffect(() => {
    fetchRecords();
  }, [filter, currentPage, pageSize]);
  
  // Update filter options when filter changes or component mounts
  useEffect(() => {
    if (user?.role) {
      updateFilterOptionsFromRecords();
    }
  }, [user?.role, updateFilterOptionsFromRecords]);

  // Form validation
  const validateFailureRate = (rate: string): boolean => {
    const numRate = parseFloat(rate);
    return !isNaN(numRate) && numRate >= 0 && numRate <= 100;
  };

  const validateNumberOfRules = (num: string): boolean => {
    const numRules = parseInt(num);
    return !isNaN(numRules) && numRules >= 1 && numRules <= 100;
  };

  // AI Generation handler
  const handleGenerateRules = async () => {
    if (!validateFailureRate(failureRate)) {
      setGenerationError('Please enter a valid failure rate between 0 and 100');
      return;
    }

    if (!validateNumberOfRules(numberOfRules)) {
      setGenerationError('Please enter a valid number of rules between 1 and 100');
      return;
    }

    if (records.length === 0) {
      setGenerationError('No data available. Please apply filters to load some data first.');
      return;
    }

    setIsGenerating(true);
    setGenerationError(null);
    setGeneratedRules([]);

    try {
      // Get all unique KPIs from the current filtered data
      const kpiDetails: { [code: string]: { category: string; values: number[] } } = {};
      
      records.forEach(record => {
        if (record.kpis) {
          record.kpis.forEach(kpi => {
            if (!kpiDetails[kpi.code]) {
              kpiDetails[kpi.code] = { category: kpi.category || 'General', values: [] };
            }
            if (typeof kpi.value === 'number') {
              kpiDetails[kpi.code].values.push(kpi.value);
            }
          });
        }
      });

      const allKPIs = Object.keys(kpiDetails);

      // Get unique brands from filtered data
      const brandSet: { [key: string]: boolean } = {};
      records.forEach(record => {
        if (record.brand) {
          brandSet[record.brand] = true;
        }
      });
      const brands = Object.keys(brandSet);
      
      const numRules = parseInt(numberOfRules);
      const targetFailureRate = parseFloat(failureRate);
      
      // Check existing weight for the primary brand to avoid constraint violations
      const primaryBrand = brands.length > 0 ? brands[0] : '';
      let existingWeight = 0;
      let availableWeight = 100;
      
      if (primaryBrand && user?.id) {
        try {
          const existingRules = await ValidationRuleService.getUserValidationRulesByBrands([primaryBrand]);
          existingWeight = existingRules.reduce((sum, rule) => sum + (rule.weight || 0), 0);
          availableWeight = 100 - existingWeight;
          
          console.log(`Existing weight for brand "${primaryBrand}": ${existingWeight}, Available: ${availableWeight}`);
          
          if (availableWeight <= 0) {
            setGenerationError(`Cannot generate rules for brand "${primaryBrand}". Total weight limit (100) already reached with existing rules. Please delete some existing rules first.`);
            return;
          }
          
          if (availableWeight < numRules) {
            setGenerationError(`Cannot generate ${numRules} rules for brand "${primaryBrand}". Only ${availableWeight} weight points available (existing rules use ${existingWeight}/100). Consider generating fewer rules or deleting existing ones.`);
            return;
          }
        } catch (error) {
          console.warn('Could not check existing rules weight, proceeding with generation:', error);
        }
      }
      
      // Prepare the request for the AI service
      const validationRequest: ValidationRuleGenerationRequest = {
        failureRate: targetFailureRate,
        numberOfRules: numRules,
        filteredData: {
          records,
          kpiDetails,
          brands,
          totalRecords: records.length
        },
        existingWeight,
        availableWeight
      };

      // Call the real AI service
      const aiGeneratedRules = await generateValidationRules(validationRequest);
      
      // Convert AI response to ValidationRule format and set brand from our data
      const formattedRules: ValidationRule[] = aiGeneratedRules.map((rule, index) => ({
        ruleText: rule.ruleText || `Generated validation rule ${index + 1}`,
        brand: primaryBrand, // Use the brand from our filtered data instead of AI response
        weight: rule.weight || 5,
        kpiCodes: JSON.stringify(rule.kpiCodes || [])
      }));

      setGeneratedRules(formattedRules);
      
    } catch (err: any) {
      setGenerationError('Failed to generate validation rules. Please try again.');
      console.error('Error generating rules:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  // Save all generated rules to backend
  const handleSaveAllRules = async () => {
    if (generatedRules.length === 0 || !user?.id) return;

    try {
      setIsGenerating(true);
      setGenerationError(null);
      
      let savedCount = 0;
      let lastError = null;
      
      // Save each rule to the backend with proper user ID
      for (const rule of generatedRules) {
        try {
          await ValidationRuleService.createValidationRule({
            ...rule,
            userId: parseInt(user.id)
          });
          savedCount++;
        } catch (ruleError: any) {
          lastError = ruleError;
          console.error(`Error saving rule "${rule.ruleText}":`, ruleError);
          
          // If it's a weight constraint error, show specific message
          if (ruleError.message && ruleError.message.includes('Total weight would exceed 100')) {
            setGenerationError(`Weight constraint violated: ${ruleError.message}. Successfully saved ${savedCount} rules before constraint was reached.`);
            break;
          }
        }
      }

      if (savedCount === generatedRules.length) {
        // All rules saved successfully
        setGeneratedRules([]);
        setFailureRate('');
        setNumberOfRules('20');
        alert(`Successfully saved all ${generatedRules.length} data quality validation rules to the database!`);
      } else if (savedCount > 0) {
        // Some rules saved, but not all
        alert(`Partially successful: Saved ${savedCount} out of ${generatedRules.length} rules. Check the error message above for details.`);
      } else {
        // No rules saved
        setGenerationError(lastError?.message || 'Failed to save validation rules to database. Please try again.');
      }
      
    } catch (err: any) {
      setGenerationError('Failed to save validation rules to database. Please try again.');
      console.error('Error saving rules:', err);
    } finally {
      setIsGenerating(false);
    }
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="page-header">
            <h1>Generate AI Validations</h1>
            <div className="header-actions">
              <button onClick={goToDashboard} className="secondary-button">
                Back to Dashboard
              </button>
            </div>
          </div>
          
          {error && (
            <div className="error-message">
              Error: {error}
            </div>
          )}
          
          <div className="process-explanation-section">
            {/* Process explanation content will be added here */}
          </div>
          
          <div className="data-filter-section">
            <FinancialRecordFilterPanel
              initialFilter={filter}
              onApplyFilter={handleApplyFilter}
              onClearFilter={handleClearFilter}
              records={allRecordsForFilters.length > 0 ? allRecordsForFilters : records}
              userMap={userMap}
              filterUserIds={filterUserIds}
              showOwnerFilter={user?.role === 'admin' || user?.role === 'group_admin'}
            />
          </div>
          
          <div className="data-grid-section">
            {isLoading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading KPI records...</p>
              </div>
            ) : records.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">📊</div>
                <h3>No KPI Records Found</h3>
                <p>No records match the current filter criteria. Try adjusting your filters or upload some data first.</p>
              </div>
            ) : (
              <>
                <div className="data-grid-container">
                  <DataGrid 
                    columns={columns}
                    rows={transformedRecords}
                    loading={isLoading}
                    height={500}
                    expandable={true}
                    renderExpandedRow={renderExpandedRow}
                    expandedRowIds={expandedRowIds}
                    onRowExpand={handleRowExpand}
                    onRowCollapse={handleRowCollapse}
                  />
                </div>
                
                {totalPages > 1 && (
                  <div className="pagination">
                    <div className="pagination-info">
                      Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} records
                    </div>
                    <div className="pagination-controls">
                      <button 
                        className="pagination-button"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <span className="pagination-page">{currentPage} of {totalPages}</span>
                      <button 
                        className="pagination-button"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          
          <div className="input-form-section">
            <h3>Generate AI Validation Rules</h3>
            <p>
              Enter the desired failure rate percentage and number of rules, and our AI will analyze your filtered data 
              to generate the specified number of relevant validation rules that would achieve approximately that failure rate.
            </p>
            
            <div className="form-group">
              <label htmlFor="failureRate">Target Failure Rate (%)</label>
              <input
                type="number"
                id="failureRate"
                value={failureRate}
                onChange={(e) => setFailureRate(e.target.value)}
                placeholder="Enter percentage (0-100)"
                min="0"
                max="100"
                step="0.1"
                disabled={isGenerating}
              />
              <small className="form-help">
                Percentage of records that should fail the generated validation rules
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="numberOfRules">Number of Rules to Generate</label>
              <input
                type="number"
                id="numberOfRules"
                value={numberOfRules}
                onChange={(e) => setNumberOfRules(e.target.value)}
                placeholder="Enter number (1-100)"
                min="1"
                max="100"
                step="1"
                disabled={isGenerating}
              />
              <small className="form-help">
                How many validation rules the AI should generate
              </small>
            </div>

            {generationError && (
              <div className="error-message">
                {generationError}
              </div>
            )}

            <div className="form-actions">
              <button 
                onClick={handleGenerateRules}
                disabled={isGenerating || !failureRate || !numberOfRules || records.length === 0}
                className={`primary-button ${isGenerating ? 'generating' : ''}`}
              >
                {isGenerating ? (
                  <>
                    <div className="spinner-small"></div>
                    Generating Rules...
                  </>
                ) : (
                  'Generate AI Validation Rules'
                )}
              </button>
            </div>

            {generatedRules.length > 0 && (
              <div className="generated-rules-section">
                <h4>Generated Data Quality Validation Rules ({generatedRules.length})</h4>
                <p>
                  Review the AI-generated data quality validation rules below. These rules are designed to identify approximately {failureRate}% of records with data quality issues. Click "Save All Rules" to add them to your validation rules library.
                </p>
                
                <ValidationRuleTable
                  rules={generatedRules}
                  loading={false}
                  onEdit={() => {}} // Disable editing for generated rules
                  onDelete={() => {}} // Disable deletion for generated rules  
                />

                <div className="save-actions">
                  <button 
                    onClick={handleSaveAllRules}
                    disabled={isGenerating}
                    className="primary-button"
                  >
                    Save All {generatedRules.length} Rules to Database
                  </button>
                  <button 
                    onClick={() => setGeneratedRules([])}
                    disabled={isGenerating}
                    className="secondary-button"
                  >
                    Clear Generated Rules
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenerateAIValidationsPage; 